import { PageBlueprint, createFrontendModule } from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { EmptyState } from '@backstage/core-components';
import {
  catalogUnprocessedEntitiesPermission,
  datadogAnalyticsPermission,
} from '../permissions';

/** `/` — reads user's landing page preference and redirects accordingly */
const rootRedirectPage = PageBlueprint.make({
  name: 'root-redirect',
  params: {
    path: '/',
    noHeader: true,
    loader: () =>
      import('../components/Root/RootRedirect').then(
        m => <m.RootRedirect />,
      ),
  },
});

/** `/onboarding` — forwards onboarding query params to the root path */
const onboardingPage = PageBlueprint.make({
  name: 'onboarding',
  params: {
    path: '/onboarding',
    noHeader: true,
    loader: () =>
      import('../components/onboarding/OnboardingRedirect').then(
        m => <m.OnboardingRedirect />,
      ),
  },
});

/** `/dashboard` — main home/dashboard page */
const dashboardPage = PageBlueprint.make({
  name: 'dashboard',
  params: {
    path: '/dashboard',
    noHeader: true,
    loader: () =>
      import('../components/dashboard/DashboardPage').then(
        m => <m.DashboardPage />,
      ),
  },
});

/** `/my-work` — personal work overview page */
const myWorkPage = PageBlueprint.make({
  name: 'my-work',
  params: {
    path: '/my-work',
    noHeader: true,
    loader: () =>
      import('../components/my-work/MyWorkPage').then(
        m => <m.MyWorkPage />,
      ),
  },
});

/** `/tools-resources` — tools and resources directory */
const toolsResourcesPage = PageBlueprint.make({
  name: 'tools-resources',
  params: {
    path: '/tools-resources',
    noHeader: true,
    loader: () =>
      import('../components/tools-resources/ToolsResourcesPage').then(
        m => <m.ToolsResourcesPage />,
      ),
  },
});

/**
 * `/catalog-unprocessed-entities` — admin-only: shows entities the catalog
 * processor has not yet successfully processed.
 * Permission check is inline so the PageBlueprint extension is always
 * discoverable by the native frontend system.
 */
const catalogUnprocessedEntitiesPage = PageBlueprint.make({
  name: 'catalog-unprocessed-entities',
  params: {
    path: '/catalog-unprocessed-entities',
    noHeader: true,
    loader: () =>
      import('../components/catalog/CustomUnprocessedEntitiesPage').then(
        m => (
          <RequirePermission
            permission={catalogUnprocessedEntitiesPermission}
            errorPage={
              <EmptyState
                title="Access Denied"
                description="You do not have permission to view unprocessed entities. This page is only available to administrators."
                missing="info"
              />
            }
          >
            <m.CustomUnprocessedEntitiesPage />
          </RequirePermission>
        ),
      ),
  },
});

/**
 * `/datadog-analytics` — admin-only: Datadog RUM analytics dashboard.
 * Permission check is inline (same reason as above).
 */
const datadogAnalyticsPage = PageBlueprint.make({
  name: 'datadog-analytics',
  params: {
    path: '/datadog-analytics',
    noHeader: true,
    loader: () =>
      import('../components/datadog/DatadogAnalyticsPage').then(
        m => (
          <RequirePermission
            permission={datadogAnalyticsPermission}
            errorPage={
              <EmptyState
                title="Access Denied"
                description="The Datadog Analytics page is only available to the Backstage administrators."
                missing="info"
              />
            }
          >
            <m.DatadogAnalyticsPage />
          </RequirePermission>
        ),
      ),
  },
});

/** `/dev/mermaid-diagram-test` — local-only TechDocs Mermaid addon smoke test */
const mermaidDiagramTestPage = PageBlueprint.make({
  name: 'mermaid-diagram-test',
  params: {
    path: '/dev/mermaid-diagram-test',
    noHeader: true,
    loader: () =>
      import('../components/techdocs/MermaidDiagramTestPage').then(
        m => <m.MermaidDiagramTestPage />,
      ),
  },
});

export const appPagesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    rootRedirectPage,
    onboardingPage,
    dashboardPage,
    myWorkPage,
    toolsResourcesPage,
    catalogUnprocessedEntitiesPage,
    datadogAnalyticsPage,
    mermaidDiagramTestPage,
  ],
});
